// Guide 16, reconstruction mode, on the Processing tab: the whole file set
// read again by one long session (read-only, from the pipeline's decodes),
// answering with a proposal and a report of gaps and conflicts. The numbers
// and the report show here; the decision is in the Review Center, and
// accepting only adds its notes (pending) - it never replaces the data.
import React, { useCallback, useEffect, useState } from "react";
import { Button, Tooltip, message } from "antd";
import { url, tokenRef } from "api";

const auth = () => ({ Authorization: `Bearer ${localStorage.getItem(tokenRef)}` });

export default function ReconstructPanel({ projectId, canEdit }) {
  const [state, setState] = useState(null);
  const [busy, setBusy] = useState(false);
  const [showReport, setShowReport] = useState(false);
  const load = useCallback(() => fetch(`${url}/projects/${projectId}/glance/reconstruct`, { headers: auth() })
    .then((r) => (r.ok ? r.json() : null)).then(setState).catch(() => setState(null)), [projectId]);
  useEffect(() => { load(); }, [load]);
  const r = state && state.latest;
  const running = r && (r.status === "queued" || r.status === "running");
  useEffect(() => { if (!running) return undefined; const t = setInterval(load, 15000); return () => clearInterval(t); }, [running, load]);
  if (!state || (!state.available && !r)) return null;

  const start = async () => {
    setBusy(true);
    try {
      const res = await fetch(`${url}/projects/${projectId}/glance/reconstruct`, { method: "POST", headers: auth() });
      const b = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(b.err || `HTTP ${res.status}`);
      message.success("Reading the whole set again; this takes up to 45 minutes");
      load();
    } catch (e) { message.error(e.message); } finally { setBusy(false); }
  };
  const c = (r && r.compare) || {};
  return (
    <div className="g-run-card">
      <div className="g-viewhead">
        <div>
          <b>Read the whole set again</b>
          <p className="g-tsub">For new file types or an odd project: every decoded document and the current building data are read again in one pass, which proposes the building data with a report of gaps and conflicts. Nothing changes until it is accepted in the Review Center, and accepting only adds its notes.</p>
        </div>
        {canEdit && state.available && (
          <Tooltip title={running ? "A reading is already running" : "Starts one reading of every decoded document"}>
            <Button size="small" loading={busy} disabled={running} onClick={start}>Read the whole set again</Button>
          </Tooltip>
        )}
      </div>
      {r && (
        <div className="g-tsub" role="status">
          {running && `Running since ${String(r.startedAt || r.createdAt).slice(0, 16).replace("T", " ")} UTC, started by ${r.startedByName || "someone"}.`}
          {r.status === "failed" && <span style={{ color: "var(--block)" }}>The last reading failed: {(r.errors || []).join("; ")}</span>}
          {r.status === "done" && (
            <>
              Last reading {String(r.finishedAt).slice(0, 16).replace("T", " ")} UTC: {r.inputs ? `${r.inputs.files} documents read; ` : ""}
              {c.levels ? `${c.levels.proposed} levels (now ${c.levels.current}); ` : ""}{r.notes} notes proposed; {r.review === "pending" ? <a href={`/approval-queue?projectId=${projectId}&tab=documentation`}>decide it in the Review Center</a> : `${r.review}${r.reviewedBy ? ` by ${r.reviewedBy}` : ""}`}.
              {r.report && <> <button type="button" className="g-linkbtn" onClick={() => setShowReport(!showReport)}>{showReport ? "Hide the report" : "Show the report"}</button></>}
              {showReport && <pre className="g-report" style={{ whiteSpace: "pre-wrap", maxHeight: 420, overflow: "auto", background: "var(--sheet)", padding: 10 }}>{r.report}</pre>}
            </>
          )}
        </div>
      )}
    </div>
  );
}
